/* Level Up · Custom savings — multiple goal-locked vaults (home / condo / car / boat / anything). */
import { useCallback, useEffect, useState } from "react";
import { api, fmtMoney, formatErr } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { Home, Car, Ship, Building2, Sparkles, Lock, Unlock, ArrowDownToLine, ArrowUpFromLine, Target, Loader2, Plus, X, Trash2, ChevronDown } from "lucide-react";
import { HomeFinder } from "@/components/HomeFinder";
import { AutofillToggle, useAutofillRules } from "@/components/VaultControls";

const KIND_ICON = { home: Home, condo: Building2, car: Car, boat: Ship, other: Sparkles };
const SHOW_MAIN = 3;

export default function NewHomeSection() {
  const { refresh } = useAuth();
  const [data, setData] = useState(null);
  const [msg, setMsg] = useState("");
  const [creating, setCreating] = useState(false);
  const [showAll, setShowAll] = useState(false);
  const autofill = useAutofillRules();

  const load = useCallback(async () => {
    try { const { data } = await api.get("/home-vaults"); setData(data); } catch { /* keep */ }
  }, []);
  useEffect(() => { load(); }, [load]);

  if (!data) return null;
  const vaults = data.vaults || [];
  const shown = showAll ? vaults : vaults.slice(0, SHOW_MAIN);
  const finderVault = vaults.find((v) => v.finder);
  const onChanged = () => { load(); autofill.reload(); refresh && refresh(); };

  return (
    <section className="mt-14" data-testid="new-home-section">
      <div className="mb-4 flex items-end justify-between gap-4 flex-wrap">
        <div>
          <div className="text-xs uppercase tracking-[0.2em] font-semibold text-sage">Level Up · Custom savings</div>
          <h2 className="font-serif text-3xl sm:text-4xl tracking-tight mt-1">Saving for something big.</h2>
          <p className="text-sm text-moss mt-2 max-w-xl">
            Each goal gets its own vault and its own target. It stays <span className="text-clay font-medium">sealed until you hit it</span> — then the whole amount releases to Spending. Homes and condos get the live finder below.
          </p>
        </div>
        <button onClick={() => setCreating(true)} className="inline-flex items-center gap-1.5 bg-ink text-cream rounded-md px-4 py-2 text-sm font-medium hover:bg-forest-deep transition-colors" data-testid="custom-savings-create-btn">
          <Plus className="w-4 h-4" /> Create your new goal
        </button>
      </div>
      {msg && <div className="mb-3 px-3 py-2 rounded-lg bg-clay-light border border-clay/40 text-xs text-clay" data-testid="new-home-msg">{msg}</div>}
      {vaults.length === 0 && (
        <div className="border border-dashed border-sand rounded-xl p-6 text-center text-sm text-sage" data-testid="custom-savings-empty">No goals yet — press "Create your new goal".</div>
      )}
      <div className="grid gap-4 items-start sm:grid-cols-2 lg:grid-cols-3" data-testid="custom-savings-list">
        {shown.map((v) => <HomeVaultCard key={v.vault_id} v={v} Icon={KIND_ICON[v.kind] || Home} onMsg={setMsg} onChanged={onChanged} autofill={autofill.rules[v.vault_id]} />)}
      </div>
      {vaults.length > SHOW_MAIN && (
        <button onClick={() => setShowAll((s) => !s)} className="mt-3 inline-flex items-center gap-1 text-xs text-forest hover:underline" data-testid="custom-savings-view-all">
          <ChevronDown className={`w-3.5 h-3.5 transition-transform ${showAll ? "rotate-180" : ""}`} /> {showAll ? "Show main three" : `View all (${vaults.length})`}
        </button>
      )}
      {finderVault && <div className="mt-6"><HomeFinder vault={finderVault} onMsg={setMsg} onVaultChanged={onChanged} /></div>}
      {creating && <CreateGoalDialog kinds={data.kinds} onClose={() => setCreating(false)} onDone={() => { setCreating(false); onChanged(); }} onMsg={setMsg} />}
    </section>
  );
}

function CreateGoalDialog({ kinds, onClose, onDone, onMsg }) {
  const [kind, setKind] = useState("home");
  const [name, setName] = useState("");
  const [target, setTarget] = useState("");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const submit = async (e) => {
    e.preventDefault(); setBusy(true); setErr("");
    try { const { data } = await api.post("/home-vaults", { kind, name: name.trim() || null, target_amount: Number(target) }); onMsg(`"${data.name}" created with a ${fmtMoney(data.target_amount)} goal.`); onDone(); }
    catch (e2) { setErr(formatErr(e2.response?.data?.detail) || e2.message); }
    finally { setBusy(false); }
  };
  return (
    <div className="fixed inset-0 bg-ink/40 backdrop-blur-sm flex items-center justify-center z-50 p-4" data-testid="custom-savings-create-dialog">
      <form onSubmit={submit} className="bg-cream rounded-2xl border border-sand max-w-md w-full p-7">
        <div className="flex items-center justify-between">
          <div className="text-xs uppercase tracking-[0.2em] font-semibold text-sage">New savings goal</div>
          <button type="button" onClick={onClose} className="text-moss hover:text-ink" data-testid="custom-savings-create-close"><X className="w-4 h-4" /></button>
        </div>
        <div className="mt-4 flex items-center gap-2 flex-wrap" data-testid="custom-savings-kinds">
          {Object.entries(kinds).map(([k, label]) => {
            const KIcon = KIND_ICON[k] || Sparkles;
            return (
              <button type="button" key={k} onClick={() => setKind(k)} aria-pressed={kind === k}
                className={`inline-flex items-center gap-1.5 rounded-full border px-3 py-1.5 text-xs font-medium transition-colors ${kind === k ? "bg-ink text-cream border-ink" : "bg-white border-sand hover:border-ink"}`}
                data-testid={`custom-savings-kind-${k}`}>
                <KIcon className="w-3.5 h-3.5" /> {label}
              </button>
            );
          })}
        </div>
        <input value={name} onChange={(e) => setName(e.target.value)} placeholder={kind === "other" ? "Name it — Wedding, RV, Studio…" : "Optional name"} maxLength={40} required={kind === "other"}
          className="mt-4 w-full bg-white border border-sand rounded-lg px-3 py-2.5 text-sm" data-testid="custom-savings-name-input" />
        <input type="number" min="1" max="500000" step="1" required value={target} onChange={(e) => setTarget(e.target.value)} placeholder="Goal amount"
          className="mt-2 w-full bg-white border border-sand rounded-lg px-3 py-2.5 text-lg font-mono" data-testid="custom-savings-target-input" />
        {err && <div className="text-sm text-clay mt-2" data-testid="custom-savings-create-error">{err}</div>}
        <button type="submit" disabled={busy || !(Number(target) > 0)} className="mt-4 w-full bg-ink text-cream rounded-lg py-3 text-sm font-medium hover:bg-forest-deep disabled:opacity-50 inline-flex items-center justify-center gap-2" data-testid="custom-savings-create-submit">
          {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Target className="w-4 h-4" />} Create goal
        </button>
      </form>
    </div>
  );
}

function HomeVaultCard({ v, Icon, onMsg, onChanged, autofill }) {
  const [target, setTarget] = useState("");
  const [amount, setAmount] = useState("");
  const [busy, setBusy] = useState("");
  const pct = Math.min(100, Math.max(0, v.progress || 0));
  const hasGoal = v.target_amount > 0;
  const q = `?vault_id=${v.vault_id}`;

  const run = async (name, fn, ok) => {
    setBusy(name); onMsg("");
    try { const { data } = await fn(); if (ok) onMsg(ok(data)); onChanged(); return true; }
    catch (e) { onMsg(formatErr(e.response?.data?.detail) || e.message); return false; }
    finally { setBusy(""); }
  };

  return (
    <div className="bg-linen border border-sand rounded-2xl p-5" data-testid={`home-vault-card-${v.vault_id}`}>
      <div className="flex items-center justify-between gap-2">
        <div className="flex items-center gap-2 text-[10px] uppercase tracking-[0.22em] font-semibold text-sage truncate"><Icon className="w-4 h-4 text-forest shrink-0" strokeWidth={1.75} /> <span className="truncate" data-testid={`home-vault-name-${v.vault_id}`}>{v.name}</span></div>
        <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-semibold uppercase tracking-wider shrink-0 ${v.unlocked ? "bg-forest text-white" : "bg-clay-light text-clay"}`} data-testid={`home-vault-state-${v.vault_id}`}>
          {v.unlocked ? <><Unlock className="w-2.5 h-2.5" /> Goal reached</> : <><Lock className="w-2.5 h-2.5" /> {hasGoal ? "Sealed until goal" : "No goal yet"}</>}
        </span>
      </div>
      <div className="mt-3 flex items-baseline justify-between">
        <div className="font-mono text-2xl text-ink" data-testid={`home-vault-balance-${v.vault_id}`}>{fmtMoney(v.balance)}</div>
        <div className="text-right text-[10px] uppercase tracking-wider font-semibold text-sage">Goal<br /><span className="font-mono text-moss text-sm normal-case tracking-normal" data-testid={`home-vault-target-${v.vault_id}`}>{hasGoal ? fmtMoney(v.target_amount) : "—"}</span></div>
      </div>
      <div className="mt-3 h-2 rounded-full bg-sand-warm overflow-hidden"><div className={`h-full ${v.unlocked ? "bg-forest" : "bg-gold"}`} style={{ width: `${pct}%` }} /></div>
      <div className="mt-1 text-[10px] text-sage font-mono">{hasGoal ? `${pct.toFixed(0)}% · ${fmtMoney(v.remaining)} to go` : "Set a goal to start"}</div>

      <div className="mt-4 flex gap-2">
        <input type="number" min="1" step="1" placeholder={hasGoal ? "Raise goal" : "Goal amount"} value={target} onChange={(e) => setTarget(e.target.value)}
          className="flex-1 min-w-0 bg-white border border-sand rounded-md px-2 py-1.5 text-sm font-mono" data-testid={`home-vault-target-input-${v.vault_id}`} />
        <button onClick={() => run("target", () => api.post(`/home-vault/target${q}`, { target_amount: Number(target) }), () => "Goal saved.").then((ok) => ok && setTarget(""))}
          disabled={busy || !(Number(target) > 0)} className="bg-ink text-white rounded-md px-3 py-1.5 text-xs font-medium hover:bg-forest-deep disabled:opacity-40 inline-flex items-center gap-1" data-testid={`home-vault-target-save-${v.vault_id}`}>
          {busy === "target" ? <Loader2 className="w-3 h-3 animate-spin" /> : <Target className="w-3 h-3" />} {hasGoal ? "Raise" : "Set"}
        </button>
      </div>
      <div className="mt-2 flex gap-2">
        <input type="number" min="20" max="500" step="1" placeholder="Deposit $20–$500" value={amount} onChange={(e) => setAmount(e.target.value)} disabled={!hasGoal}
          className="flex-1 min-w-0 bg-white border border-sand rounded-md px-2 py-1.5 text-sm font-mono disabled:opacity-50" data-testid={`home-vault-deposit-input-${v.vault_id}`} />
        <button onClick={() => run("dep", () => api.post(`/home-vault/deposit${q}`, { amount: Number(amount) }), (d) => `${fmtMoney(Number(amount))} added. ${d.unlocked ? "Goal reached — release when ready." : `${fmtMoney(d.remaining)} to go.`}`).then((ok) => ok && setAmount(""))}
          disabled={busy || !hasGoal || !(Number(amount) > 0)} className="bg-forest text-white rounded-md px-3 py-1.5 text-xs font-medium hover:bg-forest-deep disabled:opacity-40 inline-flex items-center gap-1" data-testid={`home-vault-deposit-btn-${v.vault_id}`}>
          {busy === "dep" ? <Loader2 className="w-3 h-3 animate-spin" /> : <ArrowDownToLine className="w-3 h-3" />} Deposit
        </button>
      </div>
      <div className="mt-3"><AutofillToggle vaultId={v.vault_id} rule={autofill} onChanged={onChanged} onMsg={onMsg} compact /></div>
      <div className="mt-3 flex items-center gap-2">
        <button onClick={() => run("rel", () => api.post(`/home-vault/release${q}`), (d) => `${fmtMoney(d.released)} released to Spending.`)}
          disabled={busy || !v.unlocked || v.balance <= 0} title={v.unlocked ? "" : "Sealed until you reach the goal"}
          className="flex-1 bg-transparent border border-gold text-gold rounded-md px-3 py-1.5 text-xs font-medium hover:bg-gold hover:text-white disabled:opacity-40 disabled:cursor-not-allowed inline-flex items-center justify-center gap-1.5" data-testid={`home-vault-release-btn-${v.vault_id}`}>
          {v.unlocked ? <ArrowUpFromLine className="w-3 h-3" /> : <Lock className="w-3 h-3" />} {v.unlocked ? "Release to Spending" : "Locked until goal"}
        </button>
        {v.balance <= 0 && (
          <button onClick={() => { if (window.confirm(`Remove "${v.name}"?`)) run("del", () => api.delete(`/home-vaults/${v.vault_id}`), () => "Goal removed."); }}
            className="text-sage hover:text-clay p-1.5" title="Remove goal" data-testid={`home-vault-delete-btn-${v.vault_id}`}><Trash2 className="w-3.5 h-3.5" /></button>
        )}
      </div>
    </div>
  );
}
